import React from "react";
import { X } from "react-feather";
import { useTheme } from "../theme/ThemeContext";
import { useSettings, formatAmount } from "../settings/SettingsContext";

export const useMoney = () => {
  const { settings } = useSettings();
  return (value, prefix) => formatAmount(value, settings.maskAmounts, prefix);
};

export const formatDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

export const formatDateLong = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });

export const todayIso = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const Card = ({ children, style }) => {
  const { tokens } = useTheme();
  return (
    <div
      style={{
        backgroundColor: tokens.surface,
        border: `1px solid ${tokens.surfaceBorder}`,
        borderRadius: 16,
        boxShadow: tokens.cardShadow,
        padding: 16,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

// The small action next to a section title ("See details", "All bills", "Add"):
// a tinted pill with an icon, so it reads as something to tap.
export const LinkButton = ({ icon: Icon, children, onClick, label }) => {
  const { tokens } = useTheme();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 999, border: "none", backgroundColor: tokens.accentSoft, color: tokens.textPrimary, fontSize: 13, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}
    >
      {Icon && <Icon size={15} strokeWidth={2.6} color={tokens.accent} />}
      {children}
    </button>
  );
};

export const SectionTitle = ({ children, right }) => {
  const { tokens, fonts } = useTheme();
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, margin: "22px 4px 10px" }}>
      <h2 style={{ margin: 0, fontFamily: fonts.display, fontSize: 16, color: tokens.textPrimary }}>{children}</h2>
      {right}
    </div>
  );
};

export const Chip = ({ active, onClick, children }) => {
  const { tokens } = useTheme();
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flexShrink: 0,
        padding: "8px 14px",
        borderRadius: 999,
        border: `1px solid ${active ? "transparent" : tokens.surfaceBorder}`,
        backgroundColor: active ? tokens.accent : tokens.surface,
        color: active ? tokens.accentContrast : tokens.textMuted,
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
};

export const ChipRow = ({ children }) => (
  <div style={{ display: "flex", gap: 8, overflowX: "auto", padding: "2px 2px 6px", WebkitOverflowScrolling: "touch" }}>{children}</div>
);

export const StatusPill = ({ label }) => {
  const { tokens } = useTheme();
  const map = {
    Paid: [tokens.positiveBg, tokens.positive],
    Received: [tokens.positiveBg, tokens.positive],
    Pending: [tokens.pendingBg, tokens.pending],
    Due: [tokens.negative, "#fff"],
  };
  const [bg, fg] = map[label] || [tokens.neutralSoft, tokens.textMuted];
  return (
    <span style={{ padding: "3px 9px", borderRadius: 999, fontSize: 11, fontWeight: 700, backgroundColor: bg, color: fg }}>{label}</span>
  );
};

export const Button = ({ kind = "primary", children, style, ...rest }) => {
  const { tokens } = useTheme();
  const kinds = {
    primary: { backgroundColor: tokens.accent, color: tokens.accentContrast, border: "none" },
    ghost: { backgroundColor: "transparent", color: tokens.textPrimary, border: `1px solid ${tokens.surfaceBorder}` },
    danger: { backgroundColor: "transparent", color: tokens.negative, border: `1px solid ${tokens.negative}` },
  };
  return (
    <button
      {...rest}
      style={{
        padding: "13px 16px",
        borderRadius: 12,
        fontSize: 15,
        fontWeight: 700,
        cursor: rest.disabled ? "default" : "pointer",
        opacity: rest.disabled ? 0.6 : 1,
        ...kinds[kind],
        ...style,
      }}
    >
      {children}
    </button>
  );
};

export const Field = ({ label, children }) => {
  const { tokens } = useTheme();
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <span style={{ display: "block", marginBottom: 5, fontSize: 12, fontWeight: 600, color: tokens.textMuted }}>{label}</span>
      {children}
    </label>
  );
};

export const useInputStyle = () => {
  const { tokens } = useTheme();
  return {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px 14px",
    fontSize: 16, // 16px stops iOS/Android from zooming when a field is focused
    borderRadius: 10,
    border: `1px solid ${tokens.surfaceBorder}`,
    backgroundColor: tokens.bg,
    color: tokens.textPrimary,
    outline: "none",
    fontFamily: "inherit",
  };
};

export const Sheet = ({ title, onClose, children }) => {
  const { tokens, fonts } = useTheme();
  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 2000, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-end" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          boxSizing: "border-box",
          backgroundColor: tokens.surface,
          borderRadius: "20px 20px 0 0",
          padding: "18px 16px calc(24px + env(safe-area-inset-bottom))",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontFamily: fonts.display, fontSize: 18, color: tokens.textPrimary }}>{title}</h3>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: tokens.textMuted }}>
            <X size={22} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

export const Empty = ({ children }) => {
  const { tokens } = useTheme();
  return <p style={{ textAlign: "center", color: tokens.textMuted, fontSize: 14, padding: "28px 0" }}>{children}</p>;
};
